import { clsx } from 'clsx';

import { useEffect, useRef, useState } from 'react';

import { Icons } from '@monoimin/atoms';
import { OptionalBlockWrapper } from '@monoimin/ui-core';
import {
  InputProps,
  MultiSelectOptionItem,
} from '@monoimin/forms/gravity-form/types';

import { getInputGrid, showByConditionalLogic } from '@monoimin/utils/helpers';

export const MultiSelectInput = ({
  label,
  choices,
  adminLabel,
  formValuesById,
  conditionalLogic,
  size,
  customId,
  layoutGridColumnSpan = 12,
  classNames,
}: InputProps) => {
  const [selected, setSelected] = useState<MultiSelectOptionItem[]>([]);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // скрываем выпадающий список при клике вне его
  useEffect(() => {
    function handleClickOutside(event: MouseEvent | TouchEvent): void {
      if (
        wrapperRef.current !== null &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mouseup', handleClickOutside);
    document.addEventListener('touchend', handleClickOutside);

    return () => {
      document.removeEventListener('mouseup', handleClickOutside);
      document.removeEventListener('touchend', handleClickOutside);
    };
  }, [wrapperRef]);
  if (!Array.isArray(choices)) return null;

  // скрываем поле в зависимости от условий
  const show = conditionalLogic
    ? showByConditionalLogic({ conditionalLogic, formValuesById })
    : true;
  if (!show) return null;
  const gridColumn = getInputGrid(layoutGridColumnSpan);

  const inputName = adminLabel || customId;
  const handleChange = (option: MultiSelectOptionItem) => {
    if (selected.some((opt) => opt.value === option.value)) {
      setSelected([...selected.filter((opt) => opt.value !== option.value)]);
    } else {
      setSelected([...selected, option]);
    }
  };

  return (
    <div className={clsx(classNames?.container, gridColumn)}>
      <label className={clsx('text-black-500 mb-2.5 block', classNames?.label)}>
        {label}
      </label>

      <div
        ref={wrapperRef}
        className={clsx(
          ' bg-white relative border border-line min-h-[60px] lg:w-full',
          classNames?.inputContainer,
          {
            'w-10/12': size === 'medium',
          }
        )}
      >
        <div
          className='p-2 bg-white rounded-sm w-full min-h-[60px] h-auto text-left flex flex-wrap gap-2 relative pr-10'
          onClick={() => setOpen(!open)}
        >
          {selected.length ? (
            selected.map((opt) => {
              const key = `selected-${opt?.value}`;
              return (
                <div
                  key={key}
                  data-testid={key}
                  className='flex pr-7 bg-grey border border-line h-fit relative py-0.5 pl-1.5 hover:z-50 hover:bg-grey/50 rounded-xs'
                >
                  {opt?.text}
                  <Icons
                    data-testid='close-button'
                    icon='close'
                    className='border-line absolute right-1 top-1'
                    onClick={() =>
                      setSelected(
                        selected.filter((item) => item.value !== opt?.value)
                      )
                    }
                  />
                </div>
              );
            })
          ) : (
            <p className='w-full h-full min-h-[40px] flex body items-center'>
              {
                choices?.find((opt) => opt.value.toLowerCase() === 'select')
                  ?.text
              }
            </p>
          )}
          <Icons
            icon='dropdown'
            className='border-line absolute right-3 top-3'
            sizeClassName='h-6 w-6'
          />
        </div>
        <ul
          className={clsx(
            'absolute z-10 mt-1 w-full bg-white border flex flex-col rounded shadow-lg max-h-60 overflow-auto',
            {
              hidden: !open,
            }
          )}
        >
          {!selected.length && (
            <input type='hidden' name={inputName} value='Other' />
          )}
          {choices.map((el) => {
            const isChecked = selected.some((opt) => opt.value === el.value);

            if (el.value === 'optgroup-start') {
              return (
                <div key={el.text} className='font-bold pl-5 body py-2.5'>
                  {el.text}
                </div>
              );
            }

            return (
              <OptionalBlockWrapper
                condition={
                  el.value !== 'optgroup-end' &&
                  el.value.toLowerCase() !== 'select'
                }
                dataTestId={`${el?.text}-wrapper`}
                key={el?.text}
                as='label'
                className={clsx(
                  `select-none body relative py-2.5 pl-5 pr-9 hover:bg-grey cursor-pointer`,
                  {
                    'bg-grey': isChecked,
                  }
                )}
              >
                <input
                  data-testid={`${el?.text}-checkbox`}
                  type='checkbox'
                  name={inputName}
                  value={el.value}
                  checked={isChecked}
                  onChange={() => handleChange(el)}
                  className='hidden mr-2 checked:bg-blue-500'
                />
                {el.text}
              </OptionalBlockWrapper>
            );
          })}
        </ul>
      </div>
    </div>
  );
};
